import CoursePlaceholder from "../../assets/CoursePlaceholder.png";
import Avatar from "../../assets/Avatar.png";
import ClockIcon from "../Icons/ClockIcon";
import PersonIcon from "../Icons/PersonIcon";
import { Link, useNavigate } from "react-router-dom";
import Button from "../Shared/Button";
import { CoursesDTO } from "../../api/Courses/GetAllCourses";
import { useEffect, useState } from "react";
import { getUserCountry, hasDiscount } from "../../utils/utils";
import ReactMarkdown from "react-markdown";
import { useMutation } from "@tanstack/react-query";
import { AddToCart } from "../../api/Cart/AddCourseToCard";
import { getApiErrorMessage } from "../../utils/apiError";
import { toast } from "react-toastify";
import { queryClient } from "../../main";
import { Calendar } from "lucide-react";
import { useUserDataStore } from "../../stores/user";
import { getAccessToken } from "../../lib/session";
interface CourseCardProps {
  course: CoursesDTO;
}

const CourseCard = ({ course }: CourseCardProps) => {
  const [showFullDescription] = useState(false);
  const descriptionWords = course.description.split("");
  const [country, setCountry] = useState<string | null>(null);
  const navigate = useNavigate();
  const { userData } = useUserDataStore();
  const { mutate } = useMutation({
    mutationFn: () => AddToCart(course.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Error Adding To Cart"));
    },
  });

  useEffect(() => {
    const userCountry = getUserCountry();
    setCountry(userCountry);
  }, []);
  const handleBuyNowClick = () => {
    const accessToken = getAccessToken();
    if (accessToken) {
      mutate();
    } else {
      navigate("/login?cart=true");
    }
  };

  const truncatedDescription = showFullDescription
    ? descriptionWords.join("")
    : descriptionWords.slice(1, 60).join("");
  return (
    <div className="flex flex-col justify-between h-full p-3 bg-white border md:p-4 rounded-2xl border-black/20">
      <div className="relative mb-4">
        <Link
          to={
            !course.startDate
              ? `/course/${course.slug}`
              : `/offline-course/${course.id}`
          }
        >
          <img
            className="max-h-[225px] w-full h-full object-fit rounded-lg border-[#C4C4C4]/15 border"
            src={course.img_url || CoursePlaceholder}
            alt="Course"
          />
        </Link>
        {course.part && (
          <div className="absolute w-16 h-7 text-white bg-primary top-[26px] left-px rounded-tr rounded-br flex justify-center items-center md:text-base text-sm">
            Part {course.part}
          </div>
        )}
        {course.startDate && (
          <>
            {course.isLive ? (
              <div className="absolute w-28 h-7 text-white bg-primary top-[26px] left-px rounded-tr rounded-br flex justify-center items-center md:text-base text-sm">
                Live Sessions
              </div>
            ) : (
              <div className="absolute w-32 h-7 text-white bg-secondary top-[26px] left-px rounded-tr rounded-br flex justify-center items-center md:text-base text-sm">
                Offline Sessions
              </div>
            )}
          </>
        )}
        {(country === "EG"
            ? hasDiscount(course.actualPrice_egp, course.discountPrice_egp) && (
              <div className="absolute px-2 border-red-700 h-7 border text-red-500 line-through bg-white bottom-[26px] font-semibold right-px rounded-tr rounded-br flex justify-center items-center md:text-base text-sm">
                {course.actualPrice_egp} EGP
              </div>
            )
            : hasDiscount(course.actualPrice_usd, course.discountPrice_usd) && (
              <div className="absolute px-2 border-red-700 h-7 border text-red-500 line-through bg-white bottom-[26px] font-semibold right-px rounded-tr rounded-br flex justify-center items-center md:text-base text-sm">
                {course.actualPrice_usd} USD
              </div>
            ))}
      </div>
      <div className="mt-auto">
        <div className="flex justify-between w-full items-center mb-3 py-0.5">
          <Link
            className="w-full"
            to={
              !course.startDate
                ? `/course/${course.slug}`
                : `/offline-course/${course.id}`
            }
          >
            <h1 className="text-lg font-semibold md:text-2xl sm:text-xl">
              {course.name}
            </h1>
          </Link>
          <div className="rounded py-1.5 min-w-[90px] px-3 flex justify-center md:text-base text-sm items-center gap-0.5 font-bold bg-primary text-white">
            {country === "EG" ? (
              <>
                {hasDiscount(course.actualPrice_egp, course.discountPrice_egp)
                  ? course.discountPrice_egp
                  : course.actualPrice_egp}{" "}
                <span className="text-xs uppercase md:text-sm">EGP</span>
              </>
            ) : (
              <>
                {hasDiscount(course.actualPrice_usd, course.discountPrice_usd)
                  ? course.discountPrice_usd
                  : course.actualPrice_usd}{" "}
                <span className="text-xs uppercase md:text-sm">USD</span>
              </>
            )}
          </div>
        </div>
        <div className="mb-4 text-justify">
          <span className="text-xs font-medium md:text-sm text-lightGray/70">
            <ReactMarkdown>{truncatedDescription}</ReactMarkdown>
          </span>
          {!showFullDescription && (
            <Link
              className="text-xs font-bold md:text-sm text-primary"
              to={
                !course.startDate
                  ? `/course/${course.slug}`
                  : `/offline-course/${course.id}`
              }
            >
              {" "}
              &nbsp; Learn more about {course.name}
            </Link>
          )}
        </div>
        <div className="flex items-center gap-2 mb-2 md:mb-4">
          <img
            src={course.Instructor?.photo || Avatar}
            className="md:max-w-5  md:max-h-5 max-w-[18px] max-h-[18px] w-full h-full object-cover rounded-full object-[75%_25%]"
            alt="Instructor Avatar"
          />
          <Link
            to={`/instructor/${course.InstructorId}`}
            className="text-[#1D1D1F] font-medium md:text-xs text-[10px]"
          >
            {course.Instructor?.firstName} {course.Instructor?.lastName}{" "}
            <span className="opacity-70">from</span> {course.Instructor?.city},{" "}
            {course.Instructor?.country}
          </Link>
        </div>
        <div className="flex gap-2.5 md:mb-2 mb-1.5">
          <ClockIcon />
          <span className="md:text-xs text-[10px] text-[#1D1D1F]">
            {course.num_hours} hours
          </span>
        </div>
        <div className="flex gap-2.5 mb-5 md:mb-6">
          {course._count?.students ? (
            <>
              <PersonIcon />
              <span className="md:text-xs text-[10px] text-[#1D1D1F]">
                {course._count.students} students
              </span>
            </>
          ) : (
            <>
              <Calendar size={16} />
              <span className="md:text-xs text-[10px] text-[#1D1D1F]">
                {course.startDate}
              </span>
            </>
          )}
        </div>
        <div className="flex gap-2">
          <Link
            className="w-full"
            to={
              !course.startDate
                ? `/course/${course.slug}`
                : `/offline-course/${course.id}`
            }
          >
            <Button
              variant="primary"
              className="md:h-9 h-8 !rounded-lg font-semibold text-xs w-full"
            >
              View Course
            </Button>
          </Link>
          {!course.startDate && (
            <Button
              variant="gray"
              className="md:h-9 h-8 !rounded-lg font-semibold text-xs w-full"
              onClick={handleBuyNowClick}
              disabled={userData.role === "sales"}

            >
              Add To Cart
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseCard;
